import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { announce } from '../helper/announcer';
var CELL = 110; // WORKSPACE_CELL_SIZE
var PICKER_OFFSET = 6; // px outset on each side so a target frames its tile with breathing room
// 1-based index of a tile among all colored tiles on the canvas (used for SR labels).
var getTileNumber = function (manipulative, tileId) {
    var tiles = manipulative.filter(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'coloredTiles'; });
    return tiles.findIndex(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.id) === tileId; }) + 1;
};
var buildCandidates = function (manipulative, focusedTile, mode) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    if (mode === void 0) { mode = 'group'; }
    // Snap mode: every other standalone colored tile is a valid target — the fill
    // works for any source/target mode (no groups / linear pieces).
    if (mode === 'snap') {
        return manipulative
            .filter(function (m) {
            var _a;
            return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'coloredTiles' &&
                m.data.id !== focusedTile.id &&
                !m.data.isInvisible &&
                !m.data.preview &&
                !m.data.groupId;
        })
            .map(function (m) {
            var _a;
            var t = m.data;
            return {
                kind: 'standalone',
                tileId: t.id,
                center: t.center,
                scale: (_a = t.scale) !== null && _a !== void 0 ? _a : 1,
                rows: t.rows,
                columns: t.columns,
                tileNumber: getTileNumber(manipulative, t.id),
            };
        });
    }
    var others = manipulative
        .filter(function (m) {
        var _a;
        return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'coloredTiles' &&
            m.data.id !== focusedTile.id &&
            !m.data.isInvisible &&
            !m.data.preview &&
            // Exclude other members of the focused tile's own group
            !(focusedTile.groupId && m.data.groupId === focusedTile.groupId);
    })
        .map(function (m) { return m.data; });
    var groupMap = new Map();
    var standalones = [];
    for (var _i = 0, others_1 = others; _i < others_1.length; _i++) {
        var tile = others_1[_i];
        if (tile.groupId) {
            var arr = (_a = groupMap.get(tile.groupId)) !== null && _a !== void 0 ? _a : [];
            arr.push(tile);
            groupMap.set(tile.groupId, arr);
        }
        else {
            standalones.push(tile);
        }
    }
    // Ordered list of all colored-tile group IDs on canvas (matches numbering).
    var allGroupIds = [];
    manipulative.forEach(function (m) {
        var _a, _b;
        var gId = (_a = m.data) === null || _a === void 0 ? void 0 : _a.groupId;
        if (gId && ((_b = m.data) === null || _b === void 0 ? void 0 : _b.type) === 'coloredTiles' && !allGroupIds.includes(gId)) {
            allGroupIds.push(gId);
        }
    });
    var candidates = [];
    // Groups first
    groupMap.forEach(function (members, groupId) {
        candidates.push({
            kind: 'group',
            groupId: groupId,
            groupNumber: allGroupIds.indexOf(groupId) + 1,
            tileIds: members.map(function (m) { return m.id; }),
            centers: members.map(function (m) { return m.center; }),
            scales: members.map(function (m) { var _a; return (_a = m.scale) !== null && _a !== void 0 ? _a : 1; }),
            rows: members.map(function (m) { return m.rows; }),
            columns: members.map(function (m) { return m.columns; }),
            count: members.length,
        });
    });
    // Standalones after
    for (var _m = 0, standalones_1 = standalones; _m < standalones_1.length; _m++) {
        var tile = standalones_1[_m];
        candidates.push({
            kind: 'standalone',
            tileId: tile.id,
            center: tile.center,
            scale: (_b = tile.scale) !== null && _b !== void 0 ? _b : 1,
            rows: tile.rows,
            columns: tile.columns,
            tileNumber: getTileNumber(manipulative, tile.id),
        });
    }
    // Standalone (ungrouped) linear pieces can also be group targets, so a tile can
    // be grouped directly with a linear piece via the picker.
    var allPieces = manipulative.filter(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'linearPiece'; });
    var _loop_1 = function (lp) {
        var d = lp.data;
        if (d.groupId || d.preview || d.isInvisible)
            return "continue";
        candidates.push({
            kind: 'linearPiece',
            pieceId: d.id,
            center: d.center,
            scale: (_c = d.scale) !== null && _c !== void 0 ? _c : 1,
            width: (_g = (_e = (_d = d.dimensions) === null || _d === void 0 ? void 0 : _d.width) !== null && _e !== void 0 ? _e : (_f = d.defaultDimensions) === null || _f === void 0 ? void 0 : _f.width) !== null && _g !== void 0 ? _g : CELL,
            height: (_l = (_j = (_h = d.dimensions) === null || _h === void 0 ? void 0 : _h.height) !== null && _j !== void 0 ? _j : (_k = d.defaultDimensions) === null || _k === void 0 ? void 0 : _k.height) !== null && _l !== void 0 ? _l : 20,
            pieceNumber: allPieces.findIndex(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.id) === d.id; }) + 1,
        });
    };
    for (var _o = 0, allPieces_1 = allPieces; _o < allPieces_1.length; _o++) {
        var lp = allPieces_1[_o];
        _loop_1(lp);
    }
    return candidates;
};
// All button IDs (data-btn-id values) for a candidate.
var candidateBtnIds = function (c) {
    return c.kind === 'group' ? c.tileIds : c.kind === 'linearPiece' ? [c.pieceId] : [c.tileId];
};
/**
 * Keyboard / VoiceOver picker for grouping ColoredTiles. Mirrors
 * PatternShapeLinkPicker: overlays a real <button> over every other colored
 * tile / colored-tile group inside a role="dialog". Activating a target groups
 * the source tile (or its group) with that target into a manipulativeGroup_ —
 * the SAME result as mouse multi-select + the bottom-panel Group button (both
 * funnel through the groupDiceFromMenu handler / reassignGroupPositions).
 * Real buttons + dialog is the only pattern that reliably works with VoiceOver.
 */
var ColoredTilesLinkPicker = function () {
    var _a;
    var t = useTranslation().t;
    var manipulative = useContext(DataContext).manipulative;
    var _b = useState(null), focusedTileId = _b[0], setFocusedTileId = _b[1];
    // 'group' = combine into a manipulativeGroup; 'snap' = merge tile-into-tile.
    var _c = useState('group'), mode = _c[0], setMode = _c[1];
    // Index into candidates[] — which candidate is keyboard-focused.
    var _d = useState(0), activeIdx = _d[0], setActiveIdx = _d[1];
    var containerRef = useRef(null);
    var firstBtnRef = useRef(null);
    var returnFocusIdRef = useRef(null);
    // Open: listen for the custom event dispatched by the "Group" context-menu action.
    useEffect(function () {
        var onOpen = function (data) {
            var _a;
            var nextMode = (_a = data.mode) !== null && _a !== void 0 ? _a : 'group';
            returnFocusIdRef.current = data.focusedTileId;
            setFocusedTileId(data.focusedTileId);
            setMode(nextMode);
            setActiveIdx(0);
            announce(t(nextMode === 'snap' ? 'text.coloredTilesSnapPickerOpened' : 'text.coloredTilesGroupPickerOpened'));
        };
        customEventHandler.addEventListener('openColoredTilesLinkPicker', onOpen);
        return function () { return customEventHandler.removeEventListener('openColoredTilesLinkPicker', onOpen); };
    }, [t]);
    // Derive focused tile + candidates on every render (handles source-deleted case).
    var focusedItem = focusedTileId
        ? manipulative.find(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.id) === focusedTileId; })
        : undefined;
    var focusedTile = ((_a = focusedItem === null || focusedItem === void 0 ? void 0 : focusedItem.data) === null || _a === void 0 ? void 0 : _a.type) === 'coloredTiles'
        ? focusedItem.data
        : null;
    var candidates = focusedTile ? buildCandidates(manipulative, focusedTile, mode) : [];
    // Auto-close guard: source deleted or no valid candidates.
    useEffect(function () {
        if (focusedTileId && (!focusedTile || candidates.length === 0)) {
            setFocusedTileId(null);
        }
    }, [focusedTileId, focusedTile, candidates.length]);
    // Click-outside: close the picker but let the event propagate (enables tile drag).
    useEffect(function () {
        if (!focusedTileId)
            return;
        var handleOutside = function (e) {
            var _a;
            if ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))
                return;
            setFocusedTileId(null);
        };
        window.addEventListener('pointerdown', handleOutside, { capture: true });
        return function () { return window.removeEventListener('pointerdown', handleOutside, { capture: true }); };
    }, [focusedTileId]);
    // Auto-focus the first candidate button on open. 60ms beats closeMenu's 50ms
    // focus-restore timer so the picker wins focus.
    useEffect(function () {
        if (!focusedTileId)
            return;
        var id = setTimeout(function () { var _a; return (_a = firstBtnRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 60);
        return function () { return clearTimeout(id); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedTileId]);
    if (!focusedTileId || !focusedTile || candidates.length === 0)
        return null;
    var closePicker = function () {
        setFocusedTileId(null);
        var id = returnFocusIdRef.current;
        if (id) {
            requestAnimationFrame(function () {
                var el = document.querySelector("[data-manipulative-id=\"".concat(id, "\"]"));
                el === null || el === void 0 ? void 0 : el.focus();
            });
        }
    };
    var onSelect = function (candidate) {
        var _a;
        // Snap mode: merge the source tile into the chosen target (tile-into-tile).
        if (mode === 'snap' && candidate.kind === 'standalone') {
            customEventHandler.dispatchEvent('snapColoredTileToTile', {
                movingId: focusedTileId,
                targetId: candidate.tileId,
            });
            setFocusedTileId(null);
            return;
        }
        // When the source tile is already grouped, pass its groupId so all members merge.
        var sourceId = (_a = focusedTile === null || focusedTile === void 0 ? void 0 : focusedTile.groupId) !== null && _a !== void 0 ? _a : focusedTileId;
        var diceIds = candidate.kind === 'group'
            ? [candidate.groupId, sourceId]
            : candidate.kind === 'linearPiece'
                ? [sourceId, candidate.pieceId]
                : [sourceId, candidate.tileId];
        customEventHandler.dispatchEvent('bottomPanel', {
            id: 'groupDiceFromMenu',
            panel: 'bottomPanel',
            diceIds: diceIds,
        });
        setFocusedTileId(null);
    };
    // Keyboard navigation: move between candidates (not individual buttons within a group).
    var moveCandidateFocus = function (delta) {
        var next = (activeIdx + delta + candidates.length) % candidates.length;
        setActiveIdx(next);
        var cand = candidates[next];
        var targetId = candidateBtnIds(cand)[0];
        requestAnimationFrame(function () {
            var _a;
            var btn = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("button.coloredTilesLinkPicker__target[data-btn-id=\"".concat(targetId, "\"]"));
            btn === null || btn === void 0 ? void 0 : btn.focus();
        });
    };
    var onContainerKeyDown = function (e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            closePicker();
            return;
        }
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            moveCandidateFocus(1);
        }
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            moveCandidateFocus(-1);
        }
    };
    var getCandidateIdx = function (btnId) {
        return candidates.findIndex(function (c) { return candidateBtnIds(c).includes(btnId); });
    };
    var onBtnFocus = function (btnId) {
        var idx = getCandidateIdx(btnId);
        if (idx !== -1)
            setActiveIdx(idx);
    };
    var btnCounter = 0;
    return (_jsx("div", { ref: containerRef, className: "coloredTilesLinkPicker", role: "dialog", "aria-modal": "true", "aria-label": t(mode === 'snap' ? 'text.coloredTilesSnapPickerLabel' : 'text.coloredTilesGroupPickerLabel'), onKeyDown: onContainerKeyDown, children: _jsx("div", { role: "application", "aria-label": t(mode === 'snap'
                ? 'text.coloredTilesSnapPickerLabel'
                : 'text.coloredTilesGroupPickerLabel'), style: { position: 'absolute', inset: 0 }, children: candidates.map(function (cand, candIdx) {
                var isActive = candIdx === activeIdx;
                var ids = candidateBtnIds(cand);
                var centers = cand.kind === 'group' ? cand.centers : [cand.center];
                var scales = cand.kind === 'group' ? cand.scales : [cand.scale];
                // Per-button LOGICAL (unscaled) width/height. Tiles: cols/rows × CELL.
                // Linear pieces: their own dimensions. Scale is applied per button below.
                var widthsLogical = cand.kind === 'group'
                    ? cand.columns.map(function (c) { return c * CELL; })
                    : cand.kind === 'linearPiece'
                        ? [cand.width]
                        : [cand.columns * CELL];
                var heightsLogical = cand.kind === 'group'
                    ? cand.rows.map(function (r) { return r * CELL; })
                    : cand.kind === 'linearPiece'
                        ? [cand.height]
                        : [cand.rows * CELL];
                // For group candidates only the FIRST button is SR-visible (counts as one
                // dialog item); the rest are aria-hidden click targets over the members.
                return ids.map(function (tileId, btnIdx) {
                    var _a, _b, _c, _d, _e, _f, _g;
                    var isFirstOverall = btnCounter === 0;
                    var isFirstInCand = btnIdx === 0;
                    btnCounter++;
                    var center = (_a = centers[btnIdx]) !== null && _a !== void 0 ? _a : centers[0];
                    var scale = (_c = (_b = scales[btnIdx]) !== null && _b !== void 0 ? _b : scales[0]) !== null && _c !== void 0 ? _c : 1;
                    var w = ((_e = (_d = widthsLogical[btnIdx]) !== null && _d !== void 0 ? _d : widthsLogical[0]) !== null && _e !== void 0 ? _e : CELL) * scale;
                    var h = ((_g = (_f = heightsLogical[btnIdx]) !== null && _f !== void 0 ? _f : heightsLogical[0]) !== null && _g !== void 0 ? _g : CELL) * scale;
                    // center is the geometric center → convert to top-left.
                    var left = center.x - w / 2;
                    var top = center.y - h / 2;
                    var isSrVisible = isFirstInCand;
                    var ariaLabel = isSrVisible
                        ? cand.kind === 'group'
                            ? t('text.coloredTilesGroupGroupTarget', {
                                number: cand.groupNumber,
                                count: cand.count,
                            })
                            : cand.kind === 'linearPiece'
                                ? t('text.coloredTilesGroupPieceTarget', { number: cand.pieceNumber })
                                : t(mode === 'snap'
                                    ? 'text.coloredTilesSnapTileTarget'
                                    : 'text.coloredTilesGroupTileTarget', {
                                    number: cand.tileNumber,
                                    rows: cand.rows,
                                    columns: cand.columns,
                                })
                        : undefined;
                    return (_jsx("button", { ref: isFirstOverall ? firstBtnRef : undefined, type: "button", className: "coloredTilesLinkPicker__target".concat(isActive ? ' coloredTilesLinkPicker__target--active' : ''), "data-btn-id": tileId, "data-cand-idx": candIdx, tabIndex: isSrVisible ? 0 : -1, "aria-hidden": isSrVisible ? undefined : true, "aria-label": ariaLabel, style: {
                            position: 'absolute',
                            left: left - PICKER_OFFSET,
                            top: top - PICKER_OFFSET,
                            width: w + PICKER_OFFSET * 2,
                            height: h + PICKER_OFFSET * 2,
                            zIndex: 99999,
                        }, onFocus: function () { return isSrVisible && onBtnFocus(tileId); }, onClick: function () { return onSelect(cand); }, onKeyDown: function (e) {
                            // JAWS application-mode Enter / keyboard Enter / Space. The
                            // preventDefault cancels the synthesized native click so
                            // onSelect runs exactly once. VoiceOver double-tap fires a
                            // synthetic click → onClick above.
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                onSelect(cand);
                            }
                        } }, "".concat(tileId, "-").concat(cand.kind)));
                });
            }) }) }));
};
export default ColoredTilesLinkPicker;
